import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Plus, Sparkles, CheckCheck, Trash2, Download, Loader2, Users, Star, Phone, UserPlus, Mail, ChevronDown,
} from "lucide-react";

function Stat({ icon, label, value }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs text-muted-foreground flex items-center gap-1">{icon}{label}</div>
      <div className="text-xl font-bold mt-1">{value}</div>
    </div>
  );
}

export function PoolDetailHeader({
  specialty, stats, lastSummary, enriching, enrichStatus, onEnrichRest,
  onSearch, onEnrich, onMarkSeen, onOpenCleanup, onDeleteAll, onExportCsv, onExportXlsx, onToMailing,
}) {
  const unenriched = enrichStatus?.unenriched ?? 0;
  const total = enrichStatus?.total ?? 0;
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <CardTitle className="text-xl">{specialty.display_name_de}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Gemeinsamer Pool · beide Nutzer sehen dieselben Daten</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={onSearch} data-testid="pool-search-btn">
              <Plus className="h-4 w-4 mr-2" /> Adressen suchen
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={enriching} data-testid="pool-enrich-btn">
                  {enriching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  {enriching ? "Anreichern…" : "Daten anreichern"}
                  {!enriching && <ChevronDown className="h-4 w-4 ml-2" />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Daten anreichern</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onEnrich(false)} data-testid="enrich-all-btn">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Alle Praxen anreichern
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEnrich(true)} data-testid="enrich-unenriched-btn">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Nur noch nicht angereicherte
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" onClick={onMarkSeen} data-testid="pool-mark-seen-btn">
              <CheckCheck className="h-4 w-4 mr-2" /> Als gesehen
            </Button>
            <Button variant="outline" onClick={onToMailing} data-testid="pool-to-mailing-btn">
              <Mail className="h-4 w-4 mr-2" /> Zu Rundmail
            </Button>
            <Button variant="outline" className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950/30" onClick={onOpenCleanup} data-testid="pool-cleanup-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Unvollständige entfernen
            </Button>
            <Button variant="outline" className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950/30" onClick={onDeleteAll} data-testid="pool-delete-all-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Alle löschen
            </Button>
            <Button variant="outline" onClick={onExportCsv} data-testid="export-csv-btn">
              <Download className="h-4 w-4 mr-2" /> CSV
            </Button>
            <Button variant="outline" onClick={onExportXlsx} data-testid="export-xlsx-btn">
              <Download className="h-4 w-4 mr-2" /> XLSX
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat icon={<Users className="h-4 w-4" />} label="Angezeigt" value={stats.total} />
          <Stat icon={<Star className="h-4 w-4" />} label="Neu" value={stats.neu} />
          <Stat icon={<Phone className="h-4 w-4" />} label="Kontaktiert" value={stats.kontaktiert} />
          <Stat icon={<UserPlus className="h-4 w-4" />} label="Qualifiziert" value={stats.qualifiziert} />
        </div>
        {total > 0 && (
          unenriched > 0 ? (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-lg border border-violet-200 dark:border-violet-900 bg-violet-50 dark:bg-violet-950/30 px-4 py-3 text-sm"
                 data-testid="enrich-status-bar">
              <span className="inline-flex items-center gap-2 text-violet-800 dark:text-violet-200">
                <Sparkles className="h-4 w-4 shrink-0" />
                Anreicherung: noch <strong>{unenriched}</strong> {unenriched === 1 ? "Praxis" : "Praxen"} mit Website offen
              </span>
              <Button size="sm" variant="outline" disabled={enriching} onClick={onEnrichRest}
                      className="shrink-0 border-violet-300 dark:border-violet-800" data-testid="enrich-rest-btn">
                {enriching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Rest anreichern ({unenriched})
              </Button>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 rounded-lg border border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950/30 px-4 py-2 text-sm text-green-700 dark:text-green-300"
                 data-testid="enrich-status-bar">
              <CheckCheck className="h-4 w-4 shrink-0" />
              Alle {total} Praxen angereichert
            </div>
          )
        )}
        {lastSummary && (
          <div className="rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 p-4 text-sm" data-testid="search-summary">
            <p className="font-medium mb-1">Letzte Suche · {lastSummary.searched_location}</p>
            <ul className="text-muted-foreground space-y-0.5">
              <li>{lastSummary.found_total} Treffer · {lastSummary.out_of_radius} außerhalb Radius{lastSummary.skipped_incomplete > 0 ? ` · ${lastSummary.skipped_incomplete} ohne Straße/Ort übersprungen` : ""}</li>
              <li><strong className="text-foreground">{lastSummary.added_new}</strong> neu hinzugefügt · {lastSummary.skipped_existing} bereits vorhanden (übersprungen)</li>
              {lastSummary.excluded_marked > 0 && <li>{lastSummary.excluded_marked} als „ausgeschlossen" markiert</li>}
              <li>Pool: <strong className="text-foreground">{lastSummary.total_before ?? "–"}</strong> → <strong className="text-foreground">{lastSummary.total_in_pool}</strong> Praxen{typeof lastSummary.total_before === "number" ? ` (+${lastSummary.total_in_pool - lastSummary.total_before})` : ""}</li>
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
